import { useTheme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import { useLocale } from "designSystem/locale";
import React from "react";
import { MESSAGE_TIME_FORMAT } from "src/Chat/api";
import { ContentContainer, useMessageStyles } from "src/Chat/component/styles";
import UnsupportedBlock from "src/Chat/component/UnsupportedBlock";
import * as api from "../api";
import Markdown from "./Markdown";
import { Attachment, File, isAttachmentSupported, isLayoutBlockSupported, LayoutBlock, Reaction, RichTextSectionUser } from "./RichText";
import SlackContext from "./SlackContext";

interface Props {
	channelId: string;
	message: api.Message;
}

const Message: React.FunctionComponent<Props> = ({ channelId, message }) => {
	const classes = useMessageStyles(useTheme());
	const locale = useLocale();
	let content: JSX.Element | JSX.Element[];
	let warning: JSX.Element;

	if ("channel_join" == message.subtype) {
		content = (
			<div>
				<RichTextSectionUser type={null} userId={message.user} />
				<span>{locale.resource("HasJoinedTheChannel")}</span>
			</div>
		);
	} else if ("group_join" == message.subtype) {
		content = (
			<div>
				<RichTextSectionUser type={null} userId={message.user} />
				<span>{locale.resource("HasJoinedTheGroup")}</span>
			</div>
		);
	} else if (message.blocks == null) {
		content = <div><Markdown markdown={message.text} /></div>;
	} else {
		content = message.blocks.map((block) => <LayoutBlock key={block.blockId} {...block} />);
		if (null != message.blocks.find((block) => !isLayoutBlockSupported(block)) ||
			null != message.attachments?.find((attachment) => !isAttachmentSupported(attachment))) {
			warning = (
				<UnsupportedBlock link={"https://slack.com/app_redirect?channel=" + channelId + "&message_ts=" + message.ts} linkTextProps={{ res: "UnsupportedMessageLink" }} />
			);
		}
	}

	const { usersMap } = React.useContext(SlackContext);
	const userId = message.user || message.botId;
	const userProfile = usersMap[userId]?.profile;
	const userName = api.getDisplayableUserName(userProfile) || message.username || userId;
	const userAvatarImg = userProfile?.image48;
	const date = api.ts2Date(message.ts);
	const attachments = message.attachments?.map((attachment, i) => <Attachment key={i} {...attachment} />);
	const files = message.files?.map((file) => <File file={file} key={file.id} />);
	const reactions = message.reactions?.map((reaction, index) => <Reaction key={index} reaction={reaction} />);

	const header = (
		<div style={classes.header}>
			<span style={classes.username}>{userName}</span>
			<span style={classes.timestamp}>{date.toLocaleTimeString(undefined, MESSAGE_TIME_FORMAT)}</span>
		</div>
	);
	const text = (
		<React.Fragment>
			<ContentContainer>{content}</ContentContainer>
			{attachments &&
				<div style={classes.attachment}>{attachments}</div>
			}
			{files &&
				<div style={classes.file}>{files}</div>
			}
			<div
				style={{
					width: "100%",
					overflowWrap: "break-word",
				}}
			>{warning}</div>
			{reactions &&
				<div style={classes.reaction}>{reactions}</div>
			}
		</React.Fragment>
	);

	return (
		<Grid
			container
			direction="row"
			item
			sx={classes.messageContainer}
		>
			<Avatar
				alt={userName}
				src={userAvatarImg}
				sx={classes.avatar}
			/>
			<Grid
				container
				direction="column"
				item
				sx={classes.message}
			>
				{header}
				{text}
			</Grid>
		</Grid>
	);
};

export default Message;